/** My account: where I am signed in, and ending other sessions (M02). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Monitor, Smartphone } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime, relative } from '../lib/format';
import { Alert, Button, Card, PageHeader, Skeleton, StatusChip } from '../components/ui';

interface SessionRow { id: string; userAgent: string | null; ip: string | null; createdAt: string; lastUsedAt: string; current: boolean }

function device(ua: string | null): string {
  if (!ua) return 'Unknown device';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ` on ${os}` : ''}`;
}

export function AccountPage(): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['my-sessions'], queryFn: () => api<SessionRow[]>('/auth/sessions') });
  const revoke = useMutation({ mutationFn: (id: string) => api(`/auth/sessions/${id}/revoke`, { method: 'POST' }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['my-sessions'] }) });
  return (
    <>
      <PageHeader title="My Account" description={`${user?.name} · ${user?.email}`} actions={<Link to="/change-password"><Button variant="secondary">Change password</Button></Link>} />
      <Card title="Where you are signed in" description="End any session you do not recognise, then change your password.">
        {revoke.error && <div className="mb-3"><Alert tone="critical">{(revoke.error as Error).message}</Alert></div>}
        {q.isLoading ? <Skeleton className="h-24 w-full" /> : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {q.data?.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <span className="flex items-center gap-3">
                  {/Android|iPhone|iPad/.test(s.userAgent ?? '') ? <Smartphone aria-hidden="true" className="h-5 w-5 text-slate-400" /> : <Monitor aria-hidden="true" className="h-5 w-5 text-slate-400" />}
                  <span>
                    <span className="font-medium">{device(s.userAgent)}</span> {s.current && <StatusChip tone="ok">this device</StatusChip>}
                    <span className="block text-xs text-slate-500">{s.ip ?? 'unknown address'} · signed in {dateTime(s.createdAt)} · active {relative(s.lastUsedAt)}</span>
                  </span>
                </span>
                {!s.current && <Button size="sm" variant="secondary" busy={revoke.isPending && revoke.variables === s.id} onClick={() => revoke.mutate(s.id)}>Sign out</Button>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
